import { RefObject } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Card } from '@mint-lib/tw-components';

import { useDataFileContext } from '../../contexts/DataFileContext/useDataFileContext.js';
import {
  useDataFilePreview,
  useJourneysListQuery,
  usePlatformsByCampaign,
  useUpdateDataFileContext,
} from '../../queries/index.js';
import { Combobox } from '../atoms/Combobox.jsx';

export const ContextFieldsV1 = ({
  ref,
  isUploadStep,
}: {
  ref: RefObject<HTMLDivElement | null>;
  isUploadStep: boolean;
}) => {
  const { t } = useTranslation('@myn/import-hub');

  const { dataFileId } = useDataFileContext();

  const {
    data: { context, granularityLevel },
  } = useDataFilePreview<'V1'>(dataFileId);

  const shouldShowPlatformField = granularityLevel === 'pricing_item';
  const { data: journeysList = [], isLoading: journeysListIsLoading } =
    useJourneysListQuery();

  const {
    data: platformsOfCampaign = [],
    isLoading: platformsOfCampaignIsLoading,
  } = usePlatformsByCampaign(context.journeyId as number, {
    enabled: Boolean(context.journeyId),
  });

  const { mutate: updateTableDataFileContext } =
    useUpdateDataFileContext(dataFileId);

  const handleFieldChange =
    (fieldName: keyof typeof context) =>
    (value: { id: string | number; name: string }) => {
      const payload = {
        ...context,
        [fieldName]: value?.id,
        ...(fieldName === 'journeyId' && {
          journeyName: value?.name,
          platformName: null,
        }),
      } as const;
      updateTableDataFileContext({
        id: dataFileId,
        payload,
      });
    };

  const platformNameFieldValue = platformsOfCampaign.find(
    ({ id }) => id === context.platformName,
  );

  return (
    <Card
      ref={ref}
      className="mb-3"
      contentClassName="flex items-center shrink-0 gap-4 p-3"
    >
      <Combobox
        label={t('Campaign*')}
        placeholder={t('Select')}
        inputValue={context.journeyName || null}
        onChange={handleFieldChange('journeyId')}
        options={journeysList}
        getOptionLabel={(option) => option.name}
        getOptionValue={(option) => String(option.id)}
        isLoading={journeysListIsLoading}
        withSearch
        size="small"
        color={!isUploadStep && !context.journeyName ? 'warning' : 'default'}
        className="min-w-[280px]"
        inputProps={{ autoComplete: 'off' }}
      />
      {shouldShowPlatformField && (
        <Combobox
          label={t('Platform*')}
          placeholder={t('Select')}
          inputValue={platformNameFieldValue?.name || null}
          onChange={handleFieldChange('platformName')}
          options={platformsOfCampaign}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => String(option.id)}
          isLoading={platformsOfCampaignIsLoading}
          withSearch
          disabled={!context.journeyId}
          size="small"
          color={!isUploadStep && !context.platformName ? 'warning' : 'default'}
          className="min-w-[280px]"
          inputProps={{ autoComplete: 'off' }}
        />
      )}
    </Card>
  );
};
